import { ImprovBluetoothLE } from 'improv-wifi-sdk/dist/ble';
import {
  ImprovCurrentState,
  IMPROV_BLE_SERVICE,
  IMPROV_BLE_RPC_COMMAND_CHARACTERISTIC,
  IMPROV_BLE_RPC_RESULT_CHARACTERISTIC,
} from 'improv-wifi-sdk/dist/const';
import { t } from 'src/i18n';
import { DEVICE_NAME_PREFIX } from './provisioning-contract';
import type { ProvisioningCredentials, ProvisioningStatus, WifiNetwork } from './provisioning-types';
import type { ProvisioningTransport } from './provisioning-transport';

const LOG_PREFIX = '[Signage BLE]';

// Improv ErrorState 0x03 = «unable to connect» (feil passord / nett ikke funnet).
// `const.ts` deklarerer ImprovErrorState som `const enum` (ingen runtime-verdi),
// så vi sammenligner mot tallet direkte.
const IMPROV_ERROR_UNABLE_TO_CONNECT = 3;

// Trinnvis-firmware (dabih-signage#41) implementerer to Improv RPC-kommandoer som
// `improv-wifi-sdk` ikke eksponerer (SDK-en håndterer kun enkelt-svar-RPC som
// provision). Vi snakker derfor rå GATT mot RPC-karakteristikkene på den allerede
// tilkoblede enheten:
//  - Device Info (0x03): svar `[firmware, versjon, chip, navn]` – brukes til å
//    bekrefte at vi traff riktig boks (navn == `Trinnvis-<KODE>`).
//  - Scan Wi-Fi (0x04): ett RPC Result per nett `[ssid, signal, auth]`, avsluttet
//    med en tom pakke.
const CMD_DEVICE_INFO = 0x03;
const CMD_SCAN = 0x04;
// Skann kan ta tid (radio opptatt etter tilkobling); gi opp etter dette.
const SCAN_TIMEOUT_MS = 12000;
// Device Info er ett kjapt svar; kort timeout så connect ikke henger ved taushet.
const DEVICE_INFO_TIMEOUT_MS = 4000;

function describeImprovError(errorState?: number): string {
  if (errorState === IMPROV_ERROR_UNABLE_TO_CONNECT) {
    return t('screens.networkConfig.errorWrongPassword');
  }
  return t('screens.networkConfig.errorConnectRetry');
}

// Bygg en Improv-kommandopakke: `[command][len][data…][checksum]`, der checksum
// er LSB av summen av alle foregående byte.
function buildCommandPacket(command: number, data: Uint8Array = new Uint8Array()): Uint8Array {
  const packet = new Uint8Array(data.length + 3);
  packet[0] = command;
  packet[1] = data.length;
  packet.set(data, 2);
  let sum = 0;
  for (let i = 0; i < packet.length - 1; i++) sum = (sum + packet[i]) & 0xff;
  packet[packet.length - 1] = sum;
  return packet;
}

interface RpcResult {
  command: number;
  values: string[];
}

interface RpcCharacteristics {
  command: BluetoothRemoteGATTCharacteristic;
  result: BluetoothRemoteGATTCharacteristic;
}

// Tolk en Improv RPC Result-pakke `[command][len][data…][checksum]` til kommando-
// id + lengde-prefiksede UTF-8-strenger. Returnerer `undefined` for åpenbart
// ugyldige pakker (for korte).
function parseRpcResult(value: DataView): RpcResult | undefined {
  if (value.byteLength < 2) return undefined;
  const command = value.getUint8(0);
  const dataLength = value.getUint8(1);
  const decoder = new TextDecoder();
  const values: string[] = [];
  let offset = 2;
  const end = Math.min(2 + dataLength, value.byteLength);
  while (offset < end) {
    const len = value.getUint8(offset);
    offset += 1;
    if (offset + len > value.byteLength) break;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = value.getUint8(offset + i);
    offset += len;
    values.push(decoder.decode(bytes));
  }
  return { command, values };
}

type ScanPacket = { done: true } | { done: false; network: WifiNetwork };

// Tolk ett Scan-RPC-Result. Returnerer `undefined` for pakker som ikke er vårt
// scan-svar (f.eks. et provision-result som deler samme karakteristikk).
function parseScanResult(value: DataView): ScanPacket | undefined {
  const result = parseRpcResult(value);
  if (!result || result.command !== CMD_SCAN) return undefined;
  if (result.values.length === 0) return { done: true }; // terminator = listen er ferdig

  const [ssid, signal, auth] = result.values;
  if (!ssid) return undefined;
  const signalNum = Number(signal);
  return {
    done: false,
    network: {
      ssid,
      signal: signal && !Number.isNaN(signalNum) ? signalNum : undefined,
      secured: auth ? auth.toUpperCase() === 'YES' : undefined,
    },
  };
}

/**
 * Web Bluetooth-implementasjon av provisjonerings-transporten, bygget på
 * Improv Wi-Fi-standarden via `improv-wifi-sdk` (`ImprovBluetoothLE`) for
 * connect/provision. Dekker Chrome/Edge på desktop + Chrome på Android; iOS
 * mangler Web Bluetooth → captive-portal.
 *
 * Flyt: connect → `scanWifi()` (Improv RPC Scan, rå GATT – SDK-en støtter det
 * ikke) → velg nett eller skriv inn skjult SSID → provision. Dette gir samme
 * valgmuligheter som captive-portalen.
 */
export class WebBluetoothTransport implements ProvisioningTransport {
  private device?: BluetoothDevice;
  private improv?: ImprovBluetoothLE;
  private lastStatus: ProvisioningStatus = { state: 'idle' };
  private readonly statusListeners = new Set<(status: ProvisioningStatus) => void>();

  private onImprovStateChanged = () => {
    this.emit(this.deriveStatus());
  };

  async connect(deviceCode: string): Promise<void> {
    if (!navigator.bluetooth) {
      throw new Error(t('screens.networkConfig.errorWebBluetoothUnavailable'));
    }

    if (!deviceCode.trim()) {
      throw new Error(t('screens.networkConfig.errorMissingCode'));
    }

    // Finn boksen via Improv-service-UUID-en, ikke et eksakt BLE-navn. BLE-
    // annonsen inneholder ofte ikke (eller trunkerer) det fullstendige lokale
    // navnet – det blir først pålitelig tilgjengelig etter GATT-tilkobling – så
    // et `name`/`namePrefix`-filter kan bomme selv når navnet er riktig. Det var
    // nettopp dette vi så: improv-wifi.com (som filtrerer på service-UUID-en)
    // fant boksen mens vårt navnefilter ikke gjorde det. Vi tar med
    // `Trinnvis-`-navneprefikset som et ekstra (OR) treff for firmware som
    // faktisk annonserer navnet. Improv-service ligger også i optionalServices
    // så SDK-en får tilgang etter tilkobling.
    console.info(
      `${LOG_PREFIX} Ber om boks for spillerkode ${deviceCode} (forventet navn ${DEVICE_NAME_PREFIX}${deviceCode})…`,
    );
    this.device = await navigator.bluetooth.requestDevice({
      filters: [{ services: [IMPROV_BLE_SERVICE] }, { namePrefix: DEVICE_NAME_PREFIX }],
      optionalServices: [IMPROV_BLE_SERVICE],
    });
    console.info(`${LOG_PREFIX} Valgt enhet: navn=${this.device.name ?? '(ukjent)'} id=${this.device.id}`);

    const improv = new ImprovBluetoothLE(this.device, console);
    improv.addEventListener('state-changed', this.onImprovStateChanged);
    await improv.initialize(); // kobler GATT og leser capabilities/state
    this.improv = improv;
    console.info(
      `${LOG_PREFIX} Tilkoblet «${improv.name ?? this.device.name ?? '(ukjent)'}»: ` +
        `currentState=${improv.currentState}, capabilities=${improv.capabilities}`,
    );

    // Bekreft at vi traff riktig boks via Device Info (0x03). Best-effort –
    // kaster aldri, og en mismatch logges som advarsel (men blokkerer ikke).
    await this.confirmDeviceInfo(deviceCode.trim());
  }

  /** Hent RPC Command/Result-karakteristikkene på den tilkoblede enheten. */
  private async getRpcCharacteristics(): Promise<RpcCharacteristics | undefined> {
    const gatt = this.device?.gatt;
    if (!gatt?.connected) return undefined;
    try {
      const service = await gatt.getPrimaryService(IMPROV_BLE_SERVICE);
      const command = await service.getCharacteristic(IMPROV_BLE_RPC_COMMAND_CHARACTERISTIC);
      const result = await service.getCharacteristic(IMPROV_BLE_RPC_RESULT_CHARACTERISTIC);
      return { command, result };
    } catch (err) {
      console.warn(`${LOG_PREFIX} Klarte ikke hente RPC-karakteristikker:`, err);
      return undefined;
    }
  }

  // Send Improv Device Info (0x03) og bekreft at boksens navn matcher den
  // spillerkoden vi konfigurerer (`Trinnvis-<KODE>`). Logger firmware/versjon/
  // chip og advarer ved mismatch, men avbryter aldri flyten.
  private async confirmDeviceInfo(deviceCode: string): Promise<void> {
    const chars = await this.getRpcCharacteristics();
    if (!chars) return;
    const values = await this.requestSingleRpc(chars, CMD_DEVICE_INFO, DEVICE_INFO_TIMEOUT_MS);
    if (!values) {
      console.warn(`${LOG_PREFIX} Device Info: ingen svar fra boksen (timeout).`);
      return;
    }
    const [firmware, version, chip, name] = values;
    console.info(
      `${LOG_PREFIX} Device Info: firmware=${firmware ?? '?'} v${version ?? '?'} ` +
        `chip=${chip ?? '?'} navn=${name ?? '?'}`,
    );
    const expectedName = `${DEVICE_NAME_PREFIX}${deviceCode.toUpperCase()}`;
    if (name && name.toUpperCase() !== expectedName.toUpperCase()) {
      console.warn(
        `${LOG_PREFIX} ⚠️ Boksens navn «${name}» matcher ikke forventet «${expectedName}» – ` +
          `mulig feil boks valgt.`,
      );
    } else if (name) {
      console.info(`${LOG_PREFIX} ✓ Bekreftet riktig boks: ${name}`);
    }
  }

  // Send én RPC-kommando og vent på det første svaret med samme kommando-id.
  // Returnerer svarets strenger, eller `undefined` ved timeout / skrivefeil.
  private requestSingleRpc(
    chars: RpcCharacteristics,
    command: number,
    timeoutMs: number,
  ): Promise<string[] | undefined> {
    return new Promise<string[] | undefined>((resolve) => {
      let settled = false;
      const finish = (values?: string[]) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        chars.result.removeEventListener('characteristicvaluechanged', onResult);
        resolve(values);
      };
      const onResult = (ev: Event) => {
        const value = (ev.target as BluetoothRemoteGATTCharacteristic).value;
        if (!value) return;
        const parsed = parseRpcResult(value);
        if (!parsed || parsed.command !== command) return; // annet svar – ignorér
        finish(parsed.values);
      };
      const timer = setTimeout(() => finish(undefined), timeoutMs);
      chars.result.addEventListener('characteristicvaluechanged', onResult);
      // SDK-en startet allerede notifikasjoner i initialize(); idempotent å sikre det.
      void chars.result.startNotifications().catch(() => undefined);
      void chars.command.writeValueWithoutResponse(buildCommandPacket(command)).catch((err) => {
        console.warn(`${LOG_PREFIX} Klarte ikke sende kommando 0x${command.toString(16)}:`, err);
        finish(undefined);
      });
    });
  }

  // Henter synlige WiFi-nett fra enheten via Improv RPC Scan (0x04). Enheten
  // sender ett RPC Result per nett, avsluttet med en tom pakke; vi samler dem
  // til terminatoren eller en timeout. SDK-en eksponerer ikke dette, så vi gjør
  // rå GATT mot RPC-karakteristikkene på den allerede tilkoblede enheten.
  async scanWifi(): Promise<WifiNetwork[]> {
    const chars = await this.getRpcCharacteristics();
    if (!chars) {
      console.warn(`${LOG_PREFIX} Scan utilgjengelig (ingen tilkoblet GATT/RPC) – hopper over.`);
      return [];
    }
    const { command: rpcCommand, result: rpcResult } = chars;

    const networks: WifiNetwork[] = [];
    const seen = new Set<string>();

    return await new Promise<WifiNetwork[]>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        rpcResult.removeEventListener('characteristicvaluechanged', onResult);
        // Sterkest signal øverst for en ryddig liste.
        networks.sort((a, b) => (b.signal ?? -1) - (a.signal ?? -1));
        console.info(`${LOG_PREFIX} Scan ferdig: ${networks.length} nett funnet.`);
        resolve(networks);
      };
      const onResult = (ev: Event) => {
        const value = (ev.target as BluetoothRemoteGATTCharacteristic).value;
        if (!value) return;
        const parsed = parseScanResult(value);
        if (!parsed) return; // ikke et scan-svar (f.eks. provision-result)
        if (parsed.done) {
          finish();
          return;
        }
        if (!seen.has(parsed.network.ssid)) {
          seen.add(parsed.network.ssid);
          networks.push(parsed.network);
          console.info(
            `${LOG_PREFIX} Nett: ${parsed.network.ssid} ` +
              `(signal=${parsed.network.signal ?? '?'}, sikret=${parsed.network.secured ?? '?'})`,
          );
        }
      };
      const timer = setTimeout(() => {
        console.warn(
          `${LOG_PREFIX} Scan timeout etter ${SCAN_TIMEOUT_MS} ms; bruker ${networks.length} nett så langt.`,
        );
        finish();
      }, SCAN_TIMEOUT_MS);

      rpcResult.addEventListener('characteristicvaluechanged', onResult);
      // SDK-en startet allerede notifikasjoner i initialize(); idempotent å sikre det.
      void rpcResult.startNotifications().catch(() => undefined);
      console.info(`${LOG_PREFIX} Sender Improv Scan Wi-Fi (0x04)…`);
      void rpcCommand.writeValueWithoutResponse(buildCommandPacket(CMD_SCAN)).catch((err) => {
        console.warn(`${LOG_PREFIX} Klarte ikke sende scan-kommando:`, err);
        finish();
      });
    });
  }

  async submitCredentials(credentials: ProvisioningCredentials): Promise<void> {
    const improv = this.requireImprov();
    // provision() fanger egne feil og returnerer undefined uansett utfall, så
    // vi avgjør resultatet fra current/error-state etterpå.
    await improv.provision(credentials.ssid, credentials.psk);
    if (improv.currentState === ImprovCurrentState.PROVISIONED && !improv.errorState) {
      this.emit({ state: 'connected', detail: improv.nextUrl || undefined });
      return;
    }
    const detail = describeImprovError(improv.errorState);
    this.emit({ state: 'failed', detail });
    throw new Error(detail);
  }

  onStatus(callback: (status: ProvisioningStatus) => void): () => void {
    this.statusListeners.add(callback);
    return () => {
      this.statusListeners.delete(callback);
    };
  }

  async readStatus(): Promise<ProvisioningStatus> {
    return this.improv ? this.deriveStatus() : this.lastStatus;
  }

  async disconnect(): Promise<void> {
    try {
      this.statusListeners.clear();
      if (this.improv) {
        this.improv.removeEventListener('state-changed', this.onImprovStateChanged);
        this.improv.close();
      }
      this.device?.gatt?.disconnect();
    } catch {
      // Opprydding skal aldri kaste videre.
    } finally {
      this.improv = undefined;
      this.device = undefined;
    }
  }

  private deriveStatus(): ProvisioningStatus {
    const improv = this.improv;
    if (!improv) return { state: 'idle' };
    if (improv.errorState) {
      this.lastStatus = { state: 'failed', detail: describeImprovError(improv.errorState) };
    } else if (improv.currentState === ImprovCurrentState.PROVISIONED) {
      this.lastStatus = { state: 'connected', detail: improv.nextUrl || undefined };
    } else if (improv.currentState === ImprovCurrentState.PROVISIONING) {
      this.lastStatus = { state: 'connecting' };
    } else {
      this.lastStatus = { state: 'idle' };
    }
    return this.lastStatus;
  }

  private emit(status: ProvisioningStatus): void {
    this.lastStatus = status;
    this.statusListeners.forEach((listener) => listener(status));
  }

  private requireImprov(): ImprovBluetoothLE {
    if (!this.improv) {
      throw new Error(t('screens.networkConfig.errorNotConnected'));
    }
    return this.improv;
  }
}
